/**
 * Company API — typed wrappers for company management endpoints.
 */

import { z } from "zod";
import { api, apiFetch, apiVoid } from "./client";
import {
  type Agreement,
  AgreementSchema,
  type AgreementStatus,
  AgreementStatusSchema,
  type LicenseSummary,
  LicenseSummarySchema,
  type UserActivity,
  UserActivitySchema,
  APIKeySchema,
  type APIKey,
  APIKeyCreatedSchema,
  type APIKeyCreated,
  APIKeyUsageSchema,
  type APIKeyUsage,
  AuditFilterOptionsSchema,
  type AuditFilterOptions,
  AuditLogResponseSchema,
  type AuditLogResponse,
  CompanySchema,
  type Company,
  UserSchema,
  type User,
  RoleSchema,
  type Role,
  InvitationSchema,
  type Invitation,
  SeatUsageSchema,
  type SeatUsage,
  PaginatedSchema,
  type PaginatedResponse,
} from "./schemas";

// ── Company ──────────────────────────────────────────────

export async function getCompany(): Promise<Company> {
  return api("/companies/me", CompanySchema);
}

interface CompanyUpdateData {
  name?: string;
  timezone?: string | null;
  allowed_domains?: string[];
  require_mfa?: boolean;
  password_max_age_days?: number;
  password_history_count?: number;
  security_notifications_enabled?: boolean;
  ip_allowlist_enabled?: boolean;
  ip_allowlist?: string[];
}

export async function updateCompany(
  data: CompanyUpdateData,
): Promise<Company> {
  return api("/companies/me", CompanySchema, {
    method: "PATCH",
    body: data,
  });
}

// ── Logo upload / delete (CA-scope) ─────────────────────────

export async function uploadCompanyLogo(file: File): Promise<Company> {
  const form = new FormData();
  form.append("file", file);
  return api("/companies/me/logo", CompanySchema, {
    method: "POST",
    body: form,
  });
}

export async function deleteCompanyLogo(): Promise<Company> {
  return api("/companies/me/logo", CompanySchema, {
    method: "DELETE",
  });
}

// ── Users ────────────────────────────────────────────────

export interface ListParams {
  skip?: number;
  limit?: number;
  search?: string;
}

export interface ListInvitationParams extends ListParams {
  status?: string;
}

function buildQuery(params: ListParams | ListInvitationParams): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | undefined][]) {
    if (value !== undefined && value !== null && value !== "") {
      sp.set(key, String(value));
    }
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

export async function listUsers(
  params: ListParams = {},
): Promise<PaginatedResponse<User>> {
  return api(
    `/companies/me/users${buildQuery(params)}`,
    PaginatedSchema(UserSchema),
  );
}

export async function updateUser(
  userId: string,
  data: {
    status?: string;
    is_company_admin?: boolean;
    /** Full replacement set of user-level module slugs. None / undefined
     *  leaves grants untouched. Empty array revokes everything. */
    modules?: string[];
  },
): Promise<User> {
  return api(`/companies/me/users/${userId}`, UserSchema, {
    method: "PATCH",
    body: data,
  });
}

// ── Roles ────────────────────────────────────────────────

export async function listRoles(
  params: ListParams = {},
): Promise<PaginatedResponse<Role>> {
  return api(
    `/companies/me/roles${buildQuery(params)}`,
    PaginatedSchema(RoleSchema),
  );
}

export async function createRole(data: {
  name: string;
  description?: string;
  permissions: Record<string, unknown>;
}): Promise<Role> {
  return api("/companies/me/roles", RoleSchema, {
    method: "POST",
    body: data,
  });
}

export async function updateRole(
  roleId: string,
  data: {
    name?: string;
    description?: string;
    permissions?: Record<string, unknown>;
  },
): Promise<Role> {
  return api(`/companies/me/roles/${roleId}`, RoleSchema, {
    method: "PATCH",
    body: data,
  });
}

export async function deleteRole(roleId: string): Promise<void> {
  return apiVoid(`/companies/me/roles/${roleId}`, { method: "DELETE" });
}

// ── Role assignments ─────────────────────────────────────

export async function assignRole(
  userId: string,
  roleId: string,
): Promise<void> {
  return apiVoid(`/companies/me/users/${userId}/roles`, {
    method: "POST",
    body: { role_id: roleId },
  });
}

export async function removeRole(
  userId: string,
  roleId: string,
): Promise<void> {
  return apiVoid(`/companies/me/users/${userId}/roles/${roleId}`, {
    method: "DELETE",
  });
}

// ── Invitations ──────────────────────────────────────────

export async function listInvitations(
  params: ListInvitationParams = {},
): Promise<PaginatedResponse<Invitation>> {
  return api(
    `/companies/me/invitations${buildQuery(params)}`,
    PaginatedSchema(InvitationSchema),
  );
}

export interface CreateInvitationRequest {
  email: string;
  role_id?: string;
  /** Module slugs to grant the invitee at accept time. Backend rejects
   *  any slug the company doesn't currently have. */
  modules: string[];
}

export async function createInvitation(
  data: CreateInvitationRequest,
): Promise<Invitation> {
  return api("/companies/me/invitations", InvitationSchema, {
    method: "POST",
    body: data,
  });
}

export async function getSeatUsage(): Promise<SeatUsage> {
  return api("/companies/me/seat-usage", SeatUsageSchema);
}

export async function resendInvitation(invitationId: string): Promise<void> {
  return apiVoid(`/companies/me/invitations/${invitationId}/resend`, {
    method: "POST",
  });
}

export async function revokeInvitation(invitationId: string): Promise<void> {
  return apiVoid(`/companies/me/invitations/${invitationId}/revoke`, {
    method: "POST",
  });
}

// ── Security actions ─────────────────────────────────────

export async function forceUserPasswordReset(
  userId: string,
): Promise<void> {
  return apiVoid(`/companies/me/users/${userId}/force-password-reset`, {
    method: "POST",
  });
}

export async function killUserSessions(userId: string): Promise<void> {
  return apiVoid(`/companies/me/users/${userId}/sessions`, {
    method: "DELETE",
  });
}

/** Reset a user's MFA setup. Use case: user lost their authenticator device.
 *  Disables TOTP, deletes recovery codes + WebAuthn credentials, and kills
 *  all sessions so the user must re-enroll MFA on next login. */
export async function resetUserMFA(userId: string): Promise<void> {
  return apiVoid(`/companies/me/users/${userId}/reset-mfa`, {
    method: "POST",
  });
}

export async function reactivateUser(
  userId: string,
  reason: string,
): Promise<User> {
  return api(`/companies/me/users/${userId}/reactivate`, UserSchema, {
    method: "POST",
    body: { reason },
  });
}

// ── Audit logs ──────────────────────────────────────────

export interface CompanyAuditLogParams {
  limit?: number;
  offset?: number;
  action?: string;
  action_category?: string;
  resource_type?: string;
  user_id?: string;
  since?: string;
  until?: string;
  search?: string;
}

export async function listAuditLogs(
  params: CompanyAuditLogParams = {},
): Promise<AuditLogResponse> {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return api(
    `/companies/me/audit-logs${qs ? `?${qs}` : ""}`,
    AuditLogResponseSchema,
  );
}

export async function getAuditFilterOptions(): Promise<AuditFilterOptions> {
  return api("/companies/me/audit-logs/filters", AuditFilterOptionsSchema);
}

export async function exportAuditLogsCsv(
  params: CompanyAuditLogParams = {},
): Promise<void> {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  const response = await apiFetch(
    `/companies/me/audit-logs/export${qs ? `?${qs}` : ""}`,
  );
  if (!response.ok) {
    throw new Error("Failed to export audit logs");
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = response.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ?? "audit_logs.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Agreements (ToS / DPA) ─────────────────────────────

export async function getAgreementStatus(): Promise<AgreementStatus> {
  return api("/companies/me/agreements/status", AgreementStatusSchema);
}

export async function listAgreements(): Promise<Agreement[]> {
  return api("/companies/me/agreements", z.array(AgreementSchema));
}

export async function acceptAgreement(data: {
  agreement_type: string;
  version: string;
  /** sha256 hex digest of the raw MDX content the user just read.
   *  Computed client-side via Web Crypto API at submit time. */
  document_hash: string;
  /** The exact Vertretungsmacht-Bestätigung wording the user ticked.
   *  Must match the canonical prefix + suffix for the claimed locale
   *  (see legal_manifest.REP_DECLARATION_*); the company legal name
   *  goes in the middle. The backend re-validates the round-trip and
   *  rejects with 400 invalid_representation_declaration if the text
   *  has been tampered with or is missing the canonical anchors. */
  representation_declaration_text: string;
  /** BCP-47 locale of the declaration the user read. */
  representation_declaration_locale: string;
}): Promise<Agreement> {
  return api("/companies/me/agreements", AgreementSchema, {
    method: "POST",
    body: data,
  });
}

// ── User Activity ──────────────────────────────────────

export async function getUserActivity(): Promise<UserActivity[]> {
  return api("/companies/me/users/activity", z.array(UserActivitySchema));
}

// ── License Summary ────────────────────────────────────

export async function getLicenseSummary(): Promise<LicenseSummary> {
  return api("/companies/me/license-summary", LicenseSummarySchema);
}

// ── API Keys ────────────────────────────────────────────

export async function listAPIKeys(): Promise<APIKey[]> {
  return api("/companies/me/api-keys", z.array(APIKeySchema));
}

export async function createAPIKey(data: {
  name: string;
  permissions?: Record<string, unknown>;
  allowed_ips?: string[];
  rate_limit_per_minute?: number;
  expires_at?: string;
}): Promise<APIKeyCreated> {
  return api("/companies/me/api-keys", APIKeyCreatedSchema, {
    method: "POST",
    body: data,
  });
}

export async function revokeAPIKey(keyId: string): Promise<void> {
  return apiVoid(`/companies/me/api-keys/${keyId}`, { method: "DELETE" });
}

export async function getAPIKeyUsage(keyId: string): Promise<APIKeyUsage> {
  return api(`/companies/me/api-keys/${keyId}/usage`, APIKeyUsageSchema);
}
